<template>
	<GenericForm
		ref="genericForm"
		:service="service"
		:id="id"
		:novo="novo"
		:initial-expression="criarPotencia"
		@save="(potencia) => $router.push({
			name: 'detalhar-potencia',
			params: { id: potencia._id },
		})"
		:before-save="beforeSave"
	>
		<template v-slot:title>
		{{ novo ? 'Registrar' : 'Editar' }} Potência
		</template>
		<template slot-scope="{ data: potencia }">
			<v-col cols="12" class="text-center d-flex flex-column align-center">
				<v-avatar size="90">
					<img
						v-if="logoPotencia(potencia)"
						:src="logoPotencia(potencia)"
					/>
					<v-icon
						v-else
						v-text="'mdi-web'"
						size="90"
					/>
				</v-avatar>
				<v-menu offset-y>
					<template v-slot:activator="{ on }">
						<v-btn
							outlined
							color="primary"
							v-on="on"
						>
							Alterar Logo
						</v-btn>
					</template>
					<v-list>
						<v-list-item @click="() => logoInput.click()">
							Enviar Novo
						</v-list-item>
						<v-list-item v-if="logoPotencia(potencia)" @click="() => removerLogo(potencia)">
							Remover
						</v-list-item>
					</v-list>
				</v-menu>
				<input
					type="file"
					accept="image/*"
					ref="logoInput"
					style="display: none;"
					@change="onLogoChange"
				>
			</v-col>
			<v-text-field
				label="Nome"
				v-model="potencia.nome"
				:rules="[ obrigatorio ]"
				id="id"
			/>
			<v-text-field
				:label="novo ? 'Palavra Semestral' : 'Trocar Palavra Semestral'"
				v-model="potencia.palavraSemestral"
				:rules="novo ? [ obrigatorio ] : []"
				:required="novo"
				:hint="!novo && 'Por questão de segurança não exibimos a palavra atual'"
			/>
			<v-text-field
				label="Próxima Palavra Semestral"
				v-model="potencia.proximaPalavraSemestral"
				:hint="`Essa será a nova palavra semestral após ${dataDaProximaPalavraSemestral()}`"
				persistent-hint
			/>
		</template>
  </GenericForm>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import GenericForm from '@/components/GenericForm.vue'
import PotenciaService from '@/services/PotenciaService'
import { criarPotencia, storageLogoPotenciaRef } from '@/shareds/sharedsPotencia'
import { Potencia, InputPotencia } from '../../../common/types/Potencia'
import { obrigatorio } from '@/shareds/regras'
import { storage } from '@/firebase'
import { v4 as uuidv4 } from 'uuid'
import dayjs from 'dayjs'
import { onBeforeRouteLeave } from '@/composables/router'

defineProps({
	novo: { type: Boolean, default: false },
	id: { type: String },
})

const service = PotenciaService

const genericForm = ref<{ validarSaida: () => false | void }>()
const logoInput = ref<HTMLInputElement>()

const novaLogo = ref<File | null>(null)
const logoPreview = ref('')

function logoPotencia(potencia: Potencia) {
	return logoPreview.value || potencia.urlLogo
}

function removerLogo(potencia: InputPotencia) {
	potencia.urlLogo = ''
	novaLogo.value = null
}

function onLogoChange() {
	const input = logoInput.value!
	if (!input.files) return
	const [file] = input.files
	novaLogo.value = file
}

watch(novaLogo, (logo) => {
	if (!logo) {
		logoPreview.value = ''
		return
	}
	const reader = new FileReader()

	reader.addEventListener('load', () => {
		logoPreview.value = reader.result as string
	}, false)

	reader.readAsDataURL(logo)
}, { immediate: true })

async function beforeSave(potencia: InputPotencia) {
	if (!novaLogo.value) return {
		...potencia,
	}

	const ref = storage.ref(`${storageLogoPotenciaRef}/${uuidv4()}`)
	const snapshot = await ref.put(novaLogo.value, {
		customMetadata: {
			fileName: novaLogo.value.name,
		},
	})
	const donwloadUrl = await snapshot.ref.getDownloadURL()
	return {
		...potencia,
		urlLogo: donwloadUrl || '',
	}
}

onBeforeRouteLeave((to, from, next) => {
	next(genericForm.value!.validarSaida())
})

function dataDaProximaPalavraSemestral(){
	const expirations = [
		dayjs('10', 'MM'),
		dayjs('4', 'MM'),
	]
		.map(date => date.year(new Date().getFullYear()))
		.map(date => dayjs().isAfter(date)
			? date.add(1, 'y')
			: date
		)
		.sort((dataA, dataB) => {
			return dataA.diff(dataB)
		})
	return expirations[0].format('DD/MM/YYYY')
}
</script>
